import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (FT170/FT171/FT172/FT173/FT174)
// ============================================================

export interface FT170 {
  ft170_01: string;        // Meeting code (PK)
  ft170_11: string;        // Meeting subject
  ft170_12: string;        // Description (memo)
  ft170_13: string | null; // Meeting date (ISO string)
  ft170_14: string;        // Start time
  ft170_15: string;        // End time
  ft170_16: string;        // Meeting location
  ft170_17: string;        // Chairman code
  ft170_18: number;        // Attendee count
  ft170_19: number;        // Agenda item count
  ft170_20: string;        // Chairman name
  ft170_21: string;        // Recorder code
  ft170_22: string;        // Recorder name
  ft170_24: string;        // Related code
  ft170_25: number;        // Vendor attendee count
  ft170_30: string;        // Related meeting code
  ft170_91: string;        // Modified by
  ft170_92: string | null; // Modified at
  ft170_93: string;        // Created by

  // Associations
  attendees?: FT171[];
  agendas?: FT172[];
  vendor_people?: FT174[];
}

export interface FT171 {
  ft171_01: string;        // Meeting code (FK)
  ft171_02: number;        // Sequence no
  ft171_11: string;        // Employee code
  ft171_11a: string;       // Chinese name
  ft171_11b: string;       // English name
  ft171_12: string;        // Department code
  ft171_12a: string;       // Department name
  ft171_13: string;        // Reserved
  ft171_14: string;        // Reserved
  ft171_91: string;        // Modified by
  ft171_92: string | null; // Modified at
  ft171_93: string;        // Created by
}

export interface FT172 {
  ft172_01: string;        // Meeting code (FK)
  ft172_02: number;        // Sequence no
  ft172_11: string;        // Discussion content (memo)
  ft172_12: string;        // Resolution (memo)
  ft172_91: string;        // Modified by
  ft172_92: string | null; // Modified at
  ft172_93: string;        // Created by

  // Sub-detail: tracking items
  tracking_items?: FT173[];
}

export interface FT173 {
  ft173_01: string;        // Meeting code (FK)
  ft173_02: number;        // Agenda sequence (FK)
  ft173_03: number;        // Tracking item sequence
  ft173_11: string;        // Tracking item description
  ft173_12: string | null; // Expected completion date
  ft173_13: string;        // Status / handling notes (memo)
  ft173_14: string | null; // Actual completion date
  ft173_15: string;        // Responsible person code
  ft173_15a: string;       // Responsible person name
  ft173_16: string;        // Department code
  ft173_16a: string;       // Department name
}

export interface FT174 {
  ft174_01: string;        // Meeting code (FK)
  ft174_02: number;        // Sequence no
  ft174_10: string;        // Name
  ft174_11: string;        // Title
  ft174_12: string;        // Department
  ft174_13: string;        // Vendor code
  ft174_14: string;        // Vendor name
  ft174_91: string;        // Modified by
  ft174_92: string | null; // Modified at
  ft174_93: string;        // Created by
}

// ============================================================
// Request / Response types
// ============================================================

export interface T207ListResponse {
  data: FT170[];
  total: number;
  page: number;
  size: number;
}

export interface T207CreateRequest {
  master: Partial<FT170>;
  attendees: Partial<FT171>[];
  agendas: Partial<FT172>[];
  vendor_people: Partial<FT174>[];
}

// ============================================================
// API methods
// ============================================================

export const t207Api = {
  async list(page = 1, size = 20): Promise<T207ListResponse> {
    const res = await apiClient.get('/api/t207', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FT170> {
    const res = await apiClient.get(`/api/t207/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: T207CreateRequest): Promise<FT170> {
    const res = await apiClient.post('/api/t207', data);
    return res.data;
  },

  async update(id: string, data: T207CreateRequest): Promise<FT170> {
    const res = await apiClient.put(`/api/t207/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/t207/${encodeURIComponent(id)}`);
  },

  /** Shared lookup — resolve display name for a reference code */
  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
